import {
  canonicalMonthName,
  languages,
  PhraseKey,
  phrases,
  TIME_OF_DAY_PHRASE_KEYS,
  TimeOfDay,
} from "./phrases";
import { SceneData } from "./scene";
import { SheetSubjectKind } from "./sheet-subject";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import { ScriptElement } from "./script";
import { FDXScriptElement, FDXScriptText } from "./formats/fdx";
import {
  idForDisplayName,
  normaliseSubjectName,
  parenthesisedBetween,
} from "./util";
import { Character } from "./generated/entities/project/character";

type ScriptTextAnnotation = any; // TODO

dayjs.extend(customParseFormat);
dayjs.extend(utc);

type BreakdownSheet = {
  BDSID: string;
  Scenes: string;
  SheetNumber: string;
  Synopsis: string;
  Sequence: string;
  IE: string;
  DN: string;
  Set: string;
  Location: string;
  ScriptDay: string;
  ScriptPageNumbers: string;
  NumScriptPages: string;
  Unit: string;
  EstimateTimeB: string;
  EstimateTimeA: string;
  Comments: string;
  "Cast Members"?: string[];
  Cast?: string[];
  "Background Actors"?: string[];
  Stunts?: string[];
};

function elementCategoryNames(subjectKind: SheetSubjectKind) {
  switch (subjectKind) {
    case "character":
      return ["Cast Members", "Cast", "Background Actors"];
    case "set":
      return ["Set"];
  }
}

type ElementEntry = {
  Name: string;
  CategoryName: string;
  BoardID: string;
};

type CalendarExceptions = {
  specialDays: {
    date: string;
    off: boolean;
    companyTravel: boolean;
    holiday: boolean;
    exceptionWorkday: boolean;
  }[];
  daysOff: boolean[];
};

type Calendar = {
  displayName: string;
  prepStartDate: string;
  startDate: string;
  endDate: string;
  wrapDate: string;
} & CalendarExceptions;

type CalendarEntry = string | BreakdownSheet;

type StripBoardContents = {
  days: CalendarEntry[][];
  remaining: CalendarEntry[];
};

type StripBoard = {
  displayName: string;
  calendar: Calendar;
  description: string;
  scriptVersion: string;
  scheduled: StripBoardContents;
  unscheduled: StripBoardContents;
};

type StripBoards = {
  activeStripBoard: string;
  stripBoards: Record<string, StripBoard>;
  elements?: ElementEntry[];
};

export type Modification = {
  bdsIds: string[];
  subjectKind: SheetSubjectKind;
  originalName: string;
  newName: string;
};

export type Modifications = Record<SheetSubjectKind, Modification[]>;

function possiblePhrasesFor(...phraseKeys: PhraseKey[]) {
  const prefixes = new Set<string>();
  for (const k of phraseKeys) {
    for (const phrase of Object.values(phrases[k]) as string[]) {
      prefixes.add(phrase.toLocaleUpperCase());
    }
  }
  return [...prefixes];
}

const seasonPhrases = possiblePhrasesFor("season", "series");
export const episodePhrases = possiblePhrasesFor("episode", "ep");
const txPhrases = possiblePhrasesFor("tx", "tx-date");
const endPhrases = possiblePhrasesFor("end");
const scenePhrases = possiblePhrasesFor("scene", "teaser");
const interiorPhrases = possiblePhrasesFor("interior", "int").map((s) =>
  s.replace(/\./g, ""),
);
const exteriorPhrases = possiblePhrasesFor("exterior", "ext").map((s) =>
  s.replace(/\./g, ""),
);
const todPhraseKinds = TIME_OF_DAY_PHRASE_KEYS.map((k) => ({
  k: k,
  phrases: possiblePhrasesFor(k),
}));
const dayOrdinalPhrases = possiblePhrasesFor(
  "day-ordinal",
  "day-ordinal-abbrev",
);
const teamPhrases = possiblePhrasesFor("team");

function escapeForRegExp(s: string) {
  return s.replace(/[|\\{}()[\]^$+*?.]/g, "\\$&").replace(/-/g, "\\x2d");
}

function extractPhrase(phraseAlternatives: string[], text: string) {
  const s = text.trim();
  for (const phrase of phraseAlternatives) {
    const m = s.match(
      new RegExp(
        "(^|\\W+)" + escapeForRegExp(phrase) + "(?<digits>\\d*)(\\W+|$)",
        "i",
      ),
    );
    if (m?.index !== undefined) {
      return {
        wasFound: true,
        before: s.substring(0, m.index),
        phrase,
        after: (m.groups?.digits ?? "") + s.substring(m.index + m[0].length),
      };
    }
  }
  return { wasFound: false, before: s, after: "" };
}

export function extractPrefixed(
  possiblePrefixes: string[],
  displayName: string,
  anywhere: boolean = false,
) {
  // TODO Use extractPhrase here
  const name = displayName?.trim();
  for (const prefix of possiblePrefixes) {
    const rest = name?.match(
      new RegExp(
        (anywhere ? "(^|\\W)" : "^") +
          escapeForRegExp(prefix) +
          "[\\s\\.:]*(?<rest>.*)$",
        "i",
      ),
    )?.groups?.rest;
    if (rest) return rest;
  }
  return;
}

type BannerDate = {
  prefix?: string;
  date: dayjs.Dayjs;
  suffix?: string;
};

function normaliseWeekday(lang: string, s: string | undefined) {
  s = s?.trim().toLocaleUpperCase();
  if (lang === "cy") {
    let m = s?.match(/^(.*)\s+Y$/); // TODO Abstract this to remove dangling definite article in any language
    s = m ? m[1] : s;
    m = s?.match(/^DYDD\s+(.*)$/);
    s = m ? m[1] : s;
  }
  return s;
}

function parseDateMatch(s: string, re: RegExp, prevDate?: dayjs.Dayjs) {
  // TODO Return undefined if dayjs returned invalid date
  const m = s.match(re);

  if (m) {
    const fields = m.groups!;
    const prefix = "prefix" in fields ? fields["prefix"] : undefined;
    if (prefix?.match(/\d+\/\d+/)) {
      return;
    }
    for (const { lang, months } of languages) {
      const localeData = dayjs().locale(lang).localeData();
      if (
        "weekday" in fields &&
        !localeData
          .weekdays()
          .map((s) => normaliseWeekday(lang, s))
          .filter((s) => s)
          .includes(normaliseWeekday(lang, fields["weekday"]))
      ) {
        continue;
      }
      if ("year" in fields && fields["year"] !== undefined) {
        if ("monthNumber" in fields) {
          const year =
            fields["year"].length == 2 ? `20${fields["year"]}` : fields["year"];
          return {
            prefix,
            date: dayjs(`${year}-${fields["monthNumber"]}-${fields["day"]}`),
            suffix: "suffix" in fields ? fields["suffix"] : undefined,
          };
        }
        if ("month" in fields) {
          let month = canonicalMonthName(lang, fields["month"]);
          for (const months of [
            localeData.months(),
            localeData.monthsShort(),
          ]) {
            // TODO This is because dayjs is case-sensitive. Factor this out as a dayjs patch
            for (const m of months) {
              if (month === m.toLocaleLowerCase()) month = m;
            }
          }
          const date = dayjs(
            [fields["day"], month, fields["year"]].join(" "),
            [
              "D MMMM YYYY", // TODO All 2^3 combinations of D|DD MMM|MMMM YY|YYYY
            ],
            lang,
          );
          if (date.isValid()) {
            return {
              prefix,
              date,
            };
          }
        }
      } else {
        let d: dayjs.Dayjs | undefined = dayjs(prevDate);
        if ("month" in fields && fields["month"] !== undefined) {
          const i = months
            .map((s: string) => s.toLocaleLowerCase())
            .indexOf(canonicalMonthName(lang, fields["month"]));
          if (i != -1) d = d.month(i);
          else d = undefined;
        }
        if (d) {
          d = d
            .date(parseInt(fields["day"]))
            .hour(12)
            .minute(0)
            .second(0)
            .millisecond(0);
          for (const yearOffset of [0, 1, -1]) {
            const possibleD = d.add(yearOffset, "year");
            if (
              possibleD.locale(lang).format("dddd").toLocaleUpperCase() ===
              normaliseWeekday(lang, fields["weekday"])
            ) {
              d = possibleD;
              break;
            }
          }
          return {
            prefix,
            date: d,
            suffix: fields["suffix"],
          };
        }
      }
    }
  }
  return;
}

function parseBannerDate(
  s: string,
  prevDate?: dayjs.Dayjs,
  currDate?: dayjs.Dayjs,
  exceptions?: CalendarExceptions,
): BannerDate | undefined {
  console.debug("Banner", s);
  const res =
    parseDateMatch(
      s,
      /^(?<weekday>[\w\s]+)\W?(\W+|\s+)(?<day>\d+)\S*\s+((o|O)\s+)?(?<month>\S+)\s?(?<suffix>.*)$/,
      prevDate,
    ) ||
    parseDateMatch(
      s,
      /^(?<weekday>[\w\s]+)\W?(\W+|\s+)(?<month>\S+)\s+(?<day>\d+)\S*\s?(?<suffix>.*)$/,
      prevDate,
    ) ||
    parseDateMatch(
      s,
      /^(?<prefix>.*?)(?<weekday>\w+)\W?(\W+|\s+)(?<day>\d+)[\/\.](?<monthNumber>\d+)[\/\.](?<year>\d+)\s?(?<suffix>.*)$/,
      prevDate,
    ) ||
    parseDateMatch(
      s,
      /^(?<prefix>.*?)(?<weekday>\w+)\s+(?<day>\d+)[^\d\s]*\s+(?<month>\w+)\s*(?<year>\d{4})?/,
      prevDate,
    ) ||
    parseDateMatch(
      s,
      /^(?<weekday>^[a-zA-Z\s]*[a-zA-Z])(\W|\s)+(?<day>\d+)[\/\.](?<monthNumber>\d+)[\/\.](?<year>\d+)\S*\s+(?<suffix>.*)$/,
      prevDate,
    ) ||
    parseDateMatch(
      s,
      /^(?<weekday>^\D+)\W?(\W+|\s+)(?<day>\d+)\S*\s+(?<suffix>.*)$/,
      prevDate,
    );
  if (res && !parenthesisedBetween(res.prefix, res.suffix)) return res;
  console.debug("(Not a date)");
  return;
}

function parseIdentifier(identifier: string): {
  complete: string;
  primary: string;
  suffix: string | undefined;
} {
  identifier = identifier.toLocaleUpperCase();
  const m = identifier.match(/^(?<number>\d+)(?<suffix>\D+\d*)?$/);
  if (!m)
    return { complete: identifier, primary: identifier, suffix: undefined };
  const n = parseInt(m.groups!.number);
  const numerical = n < 10 ? "0" + n : n.toString(); // TODO This assumes we want double digit padding
  const suffix = m.groups!.suffix;
  return {
    complete: numerical + (suffix ?? ""),
    ...(numerical.length
      ? { primary: numerical, suffix }
      : { primary: suffix, suffix: undefined }),
  };
}

export function processIdentifier(identifier: string) {
  return parseIdentifier(identifier).complete;
}

function processIntExt(intExt: string) {
  const parts = intExt
    .replace(/\./g, "")
    .split(/\W/)
    .map((s) => s.trim().toLocaleUpperCase());
  return {
    isInterior:
      parts.findIndex((part) => interiorPhrases.indexOf(part) != -1) != -1,
    isExterior:
      parts.findIndex((part) => exteriorPhrases.indexOf(part) != -1) != -1,
  };
}

function processTimeOfDay(timeOfDay: string) {
  const s = timeOfDay.toLocaleUpperCase().replace(/\W/g, "");
  const phraseKind = todPhraseKinds.find(
    ({ phrases }) => phrases.indexOf(s) != -1,
  );
  if (!phraseKind) return;
  return phraseKind.k;
}

function processSubjects(
  subjects: string[],
  modifications: Modification[],
  stripActorNames: boolean,
) {
  return subjects.map((s) => {
    let displayName = normaliseSubjectName(s)!;
    if (stripActorNames) {
      const separatorI = displayName.indexOf(" - ");
      if (separatorI != -1) displayName = s.substring(0, separatorI); // TODO PyC specific, but extract actor name in that case
    }
    const annotationSeparatorI = displayName.indexOf(" *");
    if (annotationSeparatorI != -1)
      displayName = s.substring(0, annotationSeparatorI); // TODO PyC specific, but extract scene notes in that case, in particular *OMMITTED*
    let modification;
    let history = [displayName];
    while (
      (modification = modifications.find(
        ({ originalName }) => originalName === displayName,
      ))
    ) {
      displayName = normaliseSubjectName(modification.newName)!;
      if (history.includes(displayName)) break;
      history.push(displayName);
    }
    return displayName;
  });
}

function sceneSuffix(sheet: BreakdownSheet) {
  const m = sheet.Sequence.match(/^[A-Z]$/); // TODO (/^([A-Z])(\s.*$|$)/);
  return m ? m[0] : "";
}

function parseScenesValue(s: string): {
  episodeId: string | undefined;
  sceneId: string | undefined;
} {
  const m = s.match(/(?<episodeId>\w+)\s*\/+\s*(?<sceneId>\w+[\w\s]*)/);
  if (m?.groups) {
    return { episodeId: m.groups.episodeId, sceneId: m.groups.sceneId.trim() };
  }
  const v = s.trim();
  if (!v.length) return { episodeId: undefined, sceneId: undefined };
  return { episodeId: undefined, sceneId: idForDisplayName(v) };
}

function dayIdentifier(scriptDay: string) {
  const m = scriptDay.match(/^(?<number>\d+)$/);
  if (m) return "D" + m.groups!.number;
  return scriptDay;
}

function sceneForBreakdownSheet(
  sheet: BreakdownSheet,
  modifications: Modifications,
  daysRatherThanEpisodes: boolean,
): SceneData | undefined {
  const { episodeId, sceneId } = parseScenesValue(sheet.Scenes);
  if (!sceneId) return;
  const [setModifications, characterModifications] = (
    ["set", "character"] as SheetSubjectKind[]
  ).map((kind) =>
    (modifications[kind] || []).filter(
      ({ bdsIds }) => bdsIds.length == 0 || bdsIds.indexOf(sheet.BDSID) != -1,
    ),
  );
  const { primary, suffix } = parseIdentifier(sceneId);
  const completeSuffix = (suffix ?? "") + sceneSuffix(sheet);
  const timeOfDay = processTimeOfDay(sheet.DN);
  const rawCast = sheet["Cast Members"] ?? sheet["Cast"];
  return {
    id: primary + completeSuffix,
    bdsId: sheet.BDSID,
    sheetNumber: sheet.SheetNumber,
    synopsis: sheet.Synopsis,
    sequence: sheet.Sequence,
    episodeId: episodeId
      ? processIdentifier(episodeId)
      : daysRatherThanEpisodes && sheet.ScriptDay
        ? dayIdentifier(sheet.ScriptDay)
        : "-",
    baseSceneId: primary,
    ...processIntExt(sheet.IE),
    ...(timeOfDay ? { timeOfDay: timeOfDay } : {}),
    sets: processSubjects([sheet.Set], setModifications, false),
    location: sheet.Location,
    scriptDay: sheet.ScriptDay,
    scriptPageNumbers: sheet.ScriptPageNumbers,
    pages: sheet.NumScriptPages,
    unit: sheet.Unit,
    comment: sheet.Comments,
    cast: processSubjects(rawCast ?? [], characterModifications, true),
    nonCast: {
      sa: processSubjects(
        (sheet["Background Actors"] ?? []).filter((s) => !rawCast?.includes(s)),
        characterModifications,
        false,
      ),
    },
  };
}

type EpisodeMetadata = { director?: string; txDate?: Date };
type Episodes = Record<string, EpisodeMetadata>;
type EpisodesAndScenes = {
  scenes: SceneData[];
  episodes: Episodes;
};

function canonicalDateString(d: any) {
  return dayjs(d).format("YYYY-MM-DD");
}

function extractTeam(s: string | undefined) {
  const teamMatch = s?.match(/\W*(?<team>[A-Za-z]+)(?<suffix>.*)/); // TODO This is RaR/PyC-specific
  if (
    teamMatch &&
    teamMatch.groups!!.suffix.includes("ON BACKLOT UNTIL" /* TODO */)
  ) {
    return;
  }
  let team = teamMatch
    ? teamMatch.groups!!.team.toLocaleUpperCase()
    : undefined;
  const possibleTeams = ["GLAS", "MELYN", "A", "B", "C", "D"]; // TODO Do this properly
  return team && possibleTeams.includes(team) ? team : undefined;
}

function toEpisodeMetadata(
  episodeId: string,
  dateString: string,
  director: string,
) {
  const date = dayjs(
    dateString,
    ["DD/MM/YYYY", "DD.MM.YY", "DD.M.YY", "D.MM.YY", "D.M.YY"],
    "en-gb",
  ); // TODO RaR-specific
  let episodeMetadata: EpisodeMetadata = director.length ? { director } : {};
  if (date.isValid()) episodeMetadata.txDate = date.toDate();
  else console.warn(`Invalid TX date ${dateString} for episode ${episodeId}`);
  return episodeMetadata;
}

function extractEpisodeMetadata(episodeId: string, s: string) {
  // TODO This is RaR-specific
  const { wasFound, before, after } = extractPhrase(txPhrases, s);
  if (!wasFound) return;
  const m = after?.match(/^(\d+\W\d+\W\d+)\W+(.*)$/);
  return (
    (m ? toEpisodeMetadata(episodeId, m[1], m[2]) : undefined) ??
    (before.match(/^\d+\W\d+\W\d+$/)
      ? toEpisodeMetadata(episodeId, before, after)
      : after.match(/^\d+\W\d+\W\d+$/)
        ? toEpisodeMetadata(episodeId, after, before)
        : undefined)
  );
}

function incorporateStripBoard(
  acc: EpisodesAndScenes,
  stripBoard: StripBoard,
  modifications: Modifications,
  daysRatherThanEpisodes: boolean,
) {
  const { calendar, scheduled, unscheduled } = stripBoard;
  const { startDate, specialDays, daysOff } = calendar;
  const extraWorkdays = specialDays
    .filter((specialDay) => specialDay.exceptionWorkday)
    .map(({ date }) => canonicalDateString(date));
  const holidays = specialDays
    .filter(({ holiday, off }) => holiday || off)
    .map(({ date }) => canonicalDateString(date));
  let currDate = dayjs(startDate);
  let currentTeam: string | undefined;
  function processEntries(
    entries: CalendarEntry[],
    filmingDatesKind: "none" | "structured" | "banner-texts",
  ) {
    let currentBannerDate;
    let indexWithinDate = 0;
    for (const entry of entries) {
      if (typeof entry === "string") {
        if (filmingDatesKind === "banner-texts") {
          let bannerDate: BannerDate | undefined;
          if (entry.toLocaleLowerCase().includes("{shooting date}")) {
            bannerDate = { date: currDate };
          } else {
            bannerDate = parseBannerDate(
              entry,
              currentBannerDate?.date ?? dayjs(startDate),
              currDate,
              { specialDays, daysOff },
            );
          }
          if (bannerDate) {
            currentBannerDate = bannerDate;
            indexWithinDate = 0;
            continue;
          }
        }
        const episodeMetadata = extractPrefixed(episodePhrases, entry);
        if (episodeMetadata) {
          const episodeM = episodeMetadata.match(/^(\d+)\W+(.*)$/);
          if (episodeM) {
            const episodeId = processIdentifier(episodeM[1]);
            const episodeMetadata = extractEpisodeMetadata(
              episodeId,
              episodeM[2],
            );
            if (episodeMetadata) {
              acc.episodes[episodeId] = episodeMetadata;
              currentBannerDate = undefined;
            }
          }
          continue;
        }
        const team = extractTeam(extractPrefixed(teamPhrases, entry));
        if (team !== undefined) {
          currentTeam = team;
          continue;
        }
        const end = extractPrefixed(endPhrases, entry);
        if (end !== undefined) {
          currentBannerDate = undefined;
          continue;
        }
      } else {
        console.debug("Scenes", entry.Scenes);
        if (entry.Scenes.length > 0) {
          const scene = sceneForBreakdownSheet(
            entry,
            modifications,
            daysRatherThanEpisodes,
          );
          const team = extractTeam(currentBannerDate?.suffix) ?? currentTeam;
          if (scene)
            acc.scenes.push({
              ...scene,
              ...(currentBannerDate
                ? {
                    filmingDate: currentBannerDate.date.toDate(),
                    orderInFilmingDate: indexWithinDate++,
                    team,
                  }
                : filmingDatesKind === "structured"
                  ? {
                      filmingDate: currDate.toDate(),
                      orderInFilmingDate: indexWithinDate++,
                    }
                  : {}),
            });
        } else {
        } // TODO Warn about this
      }
    }
  }
  function processContents(contents: StripBoardContents) {
    const { days, remaining } = contents;
    for (const day of days) {
      processEntries(day, "structured");
      let s;
      do {
        currDate = currDate.add(1, "days");
        s = canonicalDateString(currDate);
      } while (
        (daysOff[currDate.day()] && !extraWorkdays.includes(s)) ||
        holidays.includes(s)
      );
    }
    processEntries(remaining, days.length ? "none" : "banner-texts");
  }
  processContents(scheduled);
  processContents(unscheduled);
  return acc;
}

export type ProcessedSeason = {
  id: string | undefined;
  prepStartDate?: Date;
  startDate?: Date;
  endDate?: Date;
  wrapDate?: Date;
} & EpisodesAndScenes;

export function endDateFromString(s: string) {
  const d = dayjs(s).utc();
  return (
    d.hour() === 0 && d.minute() === 0 ? d.add(1, "day").subtract(1, "ms") : d
  ).toDate();
}

export function stripBoardSettings(data: StripBoards) {
  const stripBoardKeys = Object.keys(data.stripBoards);
  const activeStripBoardKey = data.activeStripBoard;
  return { stripBoardKeys, activeStripBoardKey };
}

export type ImportedSubject = {
  subjectKind: SheetSubjectKind;
  displayName: string;
  displayId: string | undefined;
  performerKind: Character["kind"] | undefined;
  sceneLabels: string[];
};

function subjectsFor(seasons: ProcessedSeason[], elements: ElementEntry[]) {
  let res: ImportedSubject[] = [];
  for (const { scenes } of seasons) {
    for (const { episodeId, id, sets, cast, nonCast } of scenes) {
      for (const [subjectKind, subjects, performerKind] of [
        ["character", cast, "cast"],
        ["set", sets, undefined],
        ["character", nonCast?.sa ?? [], "sa"],
      ] as [SheetSubjectKind, string[], Character["kind"] | undefined][]) {
        const catNames = elementCategoryNames(subjectKind);
        const catElements = elements.filter((element) =>
          catNames.includes(element.CategoryName),
        );
        for (const rawSubject of subjects) {
          const displayName = normaliseSubjectName(rawSubject)!;
          const displayId = catElements.find(
            (element) => element.Name === rawSubject,
          )?.BoardID;
          const sceneLabel = episodeId + "/" + id;
          const i = res.findIndex(
            (s) =>
              s.subjectKind === subjectKind &&
              s.displayName === displayName &&
              s.displayId === displayId,
          );
          if (i == -1)
            res.push({
              subjectKind,
              displayName,
              displayId,
              performerKind,
              sceneLabels: [sceneLabel],
            });
          else
            res[i] = {
              ...res[i],
              sceneLabels: [...res[i].sceneLabels, sceneLabel],
            };
        }
      }
    }
  }
  return res;
}

export function processStripBoards(
  data: StripBoards,
  modifications: Modifications,
  daysRatherThanEpisodes: boolean,
  selectedStripBoards: string[] | undefined,
): { seasons: ProcessedSeason[]; subjects: ImportedSubject[] } {
  const { activeStripBoard, stripBoards, elements } = data;
  const selection = (
    selectedStripBoards?.length
      ? selectedStripBoards.map((k) => stripBoards[k])
      : [stripBoards[activeStripBoard]]
  ).filter((x) => x);
  const mainStripBoard = selection.length ? selection[0] : undefined; // TODO Remove this mainStripBoard variable; we should use different calendars etc for different stripboards
  const calendar: Calendar | undefined = mainStripBoard?.calendar;
  const { episodes, scenes } = selection.reduce(
    (acc, stripBoard) =>
      incorporateStripBoard(
        acc,
        stripBoard,
        modifications,
        daysRatherThanEpisodes,
      ),
    { episodes: {}, scenes: [] } as EpisodesAndScenes,
  );
  const seasons = [
    {
      id: extractPrefixed(seasonPhrases, mainStripBoard?.displayName ?? ""),
      ...(calendar
        ? {
            prepStartDate: new Date(calendar.prepStartDate),
            startDate: new Date(calendar.startDate),
            endDate: endDateFromString(calendar.endDate),
            wrapDate: endDateFromString(calendar.wrapDate),
          }
        : {}),
      episodes,
      scenes,
    },
  ];
  const subjects = subjectsFor(seasons, elements ?? []);
  return { seasons, subjects };
}

export function modificationsFromSnapshot(snapshot: any) {
  return snapshot.docs.reduce((res: Modifications, doc: any) => {
    const modification = doc.data();
    const kind = modification.subjectKind as SheetSubjectKind;
    return { ...res, [kind]: [...(res[kind] || []), modification] };
  }, {} as Modifications);
}

function extractId(
  possiblePrefixes: string[],
  text: string,
  anywhere: boolean = false,
) {
  const suffix = extractPrefixed(possiblePrefixes, text, anywhere);
  if (!suffix) return;
  const m = suffix.match(/^(\S+?)(\W+|$)/);
  if (m) {
    const textRemainder = suffix.substring(m[0].length);
    return {
      result: processIdentifier(m[1]).toLocaleUpperCase(),
      remainder: textRemainder.length ? [{ text: textRemainder }] : [],
    };
  }
  return;
}

function flattenParagraphText(paragraph: FDXScriptText[]) {
  return paragraph.map(({ text }) => text).join("");
}

function extractIdFromParagraph(
  possiblePrefixes: string[],
  paragraph: FDXScriptText[],
  anywhere: boolean = false,
) {
  const text = flattenParagraphText(paragraph);
  for (const line of text.split("\n")) {
    const id = extractId(possiblePrefixes, line, anywhere);
    if (id) return id;
  }
  return;
}

function extractTimeOfDay(s: string): {
  before: string;
  timeOfDay: TimeOfDay | undefined;
  after: string;
} {
  for (const k of TIME_OF_DAY_PHRASE_KEYS) {
    const { wasFound, before, after } = extractPhrase(
      Object.values(phrases[k]) as string[],
      s,
    );
    if (wasFound) return { before, timeOfDay: k, after };
  }
  return { before: s, timeOfDay: undefined, after: "" };
}

function extractDate(s: string): Date | undefined {
  const m =
    parseDateMatch(
      s,
      /^(?<day>\d+)[^\d\s]*\s+(?<month>\w+)\s*(?<year>\d{4})?/, // TODO More patterns
    ) ??
    parseDateMatch(s, /^(?<month>\w+)\s*(?<day>\d+)[^\d\s]*\s+(?<year>\d{4})?/);
  return m?.date.toDate();
}

export type ScriptScene = {
  // TODO Look into merging this with SceneData
  id: string | undefined;
  episodeId: string | undefined;
  content: ScriptElement[];
  textAnnotations: ScriptTextAnnotation[];
  location?: string;
  isInterior?: boolean;
  isExterior?: boolean;
  timeOfDay?: TimeOfDay;
  cast?: string[];
  scriptDay?: string;
  scriptDate?: Date;
  dialogueLines: number;
  synopsis?: string;
  castExplicitlyListed?: boolean;
};

function addCastMembers(
  scene: ScriptScene,
  ...castMembers: Omit<ScriptTextAnnotation, "color">[]
): ScriptScene {
  if (scene.castExplicitlyListed) return scene;
  const cast = new Set(scene.cast ?? []);
  for (const s of castMembers) cast.add(s.text.trim().toLocaleUpperCase());
  return {
    ...scene,
    cast: [...cast],
    textAnnotations: [
      ...scene.textAnnotations,
      ...castMembers.map((m) => ({ ...m, color: "#00ffff" })),
    ],
  };
}

function incorporateSceneMetadata(
  scene: ScriptScene,
  contentEntry: FDXScriptElement,
  paragraph: number | undefined,
) {
  if (!("kind" in contentEntry)) return scene;
  const { kind } = contentEntry;
  const textElements = contentEntry.paragraph;
  if (kind === "Cast") {
    const cast = flattenParagraphText(textElements)
      .toLocaleUpperCase()
      .trim()
      .split(/\s*\/\s*\+?\s*/);
    return { ...scene, cast, castExplicitlyListed: true };
  }
  if (kind === "Character" && textElements.length && paragraph !== undefined) {
    const { text } = textElements[0];
    return addCastMembers(scene, {
      paragraph,
      element: 0,
      text,
      range: { start: 0, length: text.length },
    });
  }
  if (kind === "Scene Heading") {
    const text = flattenParagraphText(textElements);
    const interior = extractPhrase(interiorPhrases, text);
    const exterior = extractPhrase(
      exteriorPhrases,
      interior.wasFound ? interior.after : interior.before,
    );
    const dayOrdinal = extractPhrase(
      dayOrdinalPhrases,
      exterior.wasFound ? exterior.after : exterior.before,
    );
    const dayOrdinalMatch = dayOrdinal.wasFound
      ? dayOrdinal.after.match(/^(?<day>\d+)(?<remainder>.*)$/)
      : undefined;
    const remainder = dayOrdinal.wasFound
      ? dayOrdinalMatch
        ? dayOrdinalMatch.groups!!.remainder
        : dayOrdinal.after
      : dayOrdinal.before;
    const { timeOfDay, before, after } = extractTimeOfDay(remainder);
    const scriptDate = extractDate(after.length ? after : remainder);
    const scriptDay = dayOrdinalMatch
      ? dayOrdinalMatch.groups!!.day
      : undefined;
    return {
      ...scene,
      isInterior: interior.wasFound,
      isExterior: exterior.wasFound,
      scriptDay: scriptDay
        ? scriptDay.match(/^\d/)
          ? phrases["day-ordinal-abbrev"]["en"] /* TODO */ + scriptDay
          : scriptDay
        : undefined,
      scriptDate,
      location: (dayOrdinal.wasFound
        ? dayOrdinal.before
        : before
      ).toLocaleUpperCase(),
      timeOfDay,
    };
  }
  if (kind === "Summary") {
    if (scene.synopsis) return scene;
    return { ...scene, synopsis: flattenParagraphText(textElements).trim() };
  }
  if (kind === "Time") {
    if (scene.scriptDay) return scene;
    return { ...scene, scriptDay: flattenParagraphText(textElements).trim() };
  }
  return scene;
}

export function processScript(
  script: FDXScriptElement[],
  episodeId?: string | undefined,
  sceneId?: string | undefined,
) {
  let dialogueLines = 0;
  let scenes: ScriptScene[] = [];
  function emptyScene(id: string | undefined): ScriptScene {
    return {
      id,
      episodeId,
      content: [],
      textAnnotations: [],
      dialogueLines: 0,
    };
  }
  let scene = emptyScene(sceneId);
  let started = false;
  let manualSceneNumbering = false;
  function pushScene(nextSceneId?: string | undefined) {
    const id = processIdentifier(scene.id ?? (scenes.length + 1).toString());
    const existingIdx = scenes.findIndex((scene) => scene.id === id);
    if (existingIdx !== -1) {
      console.warn(`Overwriting duplicate scene ${id}`); // TODO Warn user
      scenes.splice(existingIdx, 1);
    }
    scenes.push({
      ...scene,
      id,
    });
    scene = emptyScene(nextSceneId);
  }
  for (const contentEntry of script) {
    let dialogueLineNumber: number | undefined = undefined;
    const kind = "kind" in contentEntry ? contentEntry.kind : undefined;
    const paragraph =
      "paragraph" in contentEntry ? contentEntry.paragraph : undefined;
    if (kind === "General" && paragraph) {
      episodeId =
        episodeId ?? extractIdFromParagraph(episodePhrases, paragraph)?.result;
    } else if (kind === "Penawd" && paragraph) {
      // TODO This is specific to RaR
      const episodeMatch = extractIdFromParagraph(episodePhrases, paragraph);
      episodeId = episodeId || episodeMatch?.result;
      const sceneMatch = extractIdFromParagraph(
        scenePhrases,
        episodeMatch ? episodeMatch.remainder : paragraph,
        true,
      );
      if (sceneMatch) {
        started = true;
        manualSceneNumbering = true;
        if (scene.id) pushScene(sceneMatch.result);
        else scene.id = sceneMatch.result;
      }
    } else if (
      !manualSceneNumbering &&
      kind === "Scene Heading" &&
      "paragraph" in contentEntry
    ) {
      started = true;
      if (scene.content.length) pushScene(contentEntry.number);
      else scene.id = contentEntry.number;
    } else if (kind === "Dialogue" && !contentEntry.dualCharacter) {
      if (
        scene &&
        paragraph
          ?.map((elt: any) => ("text" in elt ? elt.text : ""))
          .join("")
          .trim().length
      ) {
        dialogueLines++;
        scene.dialogueLines++;
        dialogueLineNumber = scene.dialogueLines;
      }
    }

    scene = incorporateSceneMetadata(
      scene,
      contentEntry,
      started ? scene.content.length : undefined,
    );
    if (started)
      scene.content.push({
        ...contentEntry,
        ...(dialogueLineNumber === undefined ? {} : { dialogueLineNumber }),
      });
  }
  if (scene.content.length) pushScene();
  return {
    episodeId: episodeId,
    dialogueLines: dialogueLines,
    scenes: scenes,
  };
}
